import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { WEIGHING_STATUSES } from '../../TransportCloudTable.consts';

interface EditableWeighingInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
}

const options = [
  { value: 'AT_LOADING', label: WEIGHING_STATUSES.atLoading },
  { value: 'AT_PORT', label: WEIGHING_STATUSES.atPort },
  { value: 'OTHER_ADDRESS', label: WEIGHING_STATUSES.otherAddress },
];

const parseInitialValue = (val: any): string | null => {
  if (!val) return null;

  if (typeof val === 'string') {
    if (val === WEIGHING_STATUSES.atLoading) return 'AT_LOADING';
    if (val === WEIGHING_STATUSES.atPort) return 'AT_PORT';
    if (val === WEIGHING_STATUSES.otherAddress) return 'OTHER_ADDRESS';
  }

  return null;
};

export const EditableWeighingInput: React.FC<EditableWeighingInputProps> = ({
  value: initialValue,
  onChange,
  onSave,
  onCancel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasSavedRef = useRef<boolean>(false);
  const [localValue, setLocalValue] = useState(() =>
    parseInitialValue(initialValue),
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') onCancel();
    else if (event.key === 'Enter') onSave(localValue);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (hasSavedRef.current) {
        hasSavedRef.current = false;
        return;
      }

      if (!containerRef.current?.contains(document.activeElement)) {
        onSave(localValue);
      }
    }, 200);
  };

  return (
    <Box
      ref={containerRef}
      display='flex'
      gap={0.5}
      alignItems='center'
      onKeyDown={handleKeyDown}
      sx={{
        width: '220px',
        height: 'auto',
        maxHeight: '25px',
        '& .MuiOutlinedInput-root': {
          height: '24px',
          minHeight: '24px',
          padding: '0px 8px',
          fontSize: '0.7rem',
        },
        '& .MuiInputBase-input': {
          padding: '0px',
          height: '24px',
          lineHeight: '1.5em',
          fontSize: '0.7rem',
        },
        '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 10px)' },
        '& .MuiAutocomplete-popupIndicator': {
          padding: '1px',
          '& .MuiSvgIcon-root': { fontSize: '12px' },
        },
      }}
    >
      <AutocompletePaginationAsync
        padding='dense'
        disableClearable
        value={options.find((opt) => opt.value === localValue) || null}
        {...({
          componentsProps: {
            paper: {
              sx: {
                '& .MuiAutocomplete-option': {
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  minHeight: '28px',
                },
                '& .MuiAutocomplete-noOptions': {
                  fontSize: '0.7rem',
                  padding: '4px 8px',
                },
              },
            },
          },
        } as any)}
        loading={false}
        options={options}
        isOptionEqualToValue={(option, selectedValue) =>
          option?.value === selectedValue?.value
        }
        getOptionLabel={(option) => option?.label || ''}
        handleSearchChange={() => {}}
        onChange={(_, selectedStatus) => {
          const newValue = selectedStatus?.value || null;
          setLocalValue(newValue);
          onChange(newValue);
          hasSavedRef.current = true;
          onSave(newValue);
        }}
        onScrollBottom={() => {}}
        onBlur={handleBlur}
      />
    </Box>
  );
};
